import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Activity, CircleCheck, CircleAlert, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const token = params.get('token');
  const [state, setState] = useState(token ? 'loading' : 'error');
  const [message, setMessage] = useState(token ? '' : 'Kein Bestätigungs-Link gefunden.');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    authAPI.verifyEmail(token)
      .then((res) => {
        applySession(res.data);
        setState('success');
        setTimeout(() => navigate('/dashboard'), 1800);
      })
      .catch((err) => {
        setState('error');
        setMessage(err.response?.data?.detail || 'Bestätigung fehlgeschlagen.');
      });
  }, [token, applySession, navigate]);

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await authAPI.resendVerification(email);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erneutes Senden fehlgeschlagen');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12" data-testid="verify-email-page">
      <div className="w-full max-w-md text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-8 neon-glow">
          <Activity className="w-7 h-7 text-primary" />
        </div>

        {state === 'loading' && (
          <div data-testid="verify-loading">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl mb-2">E-Mail wird bestätigt…</h2>
          </div>
        )}

        {state === 'success' && (
          <div data-testid="verify-success">
            <CircleCheck className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl mb-2">E-Mail bestätigt</h2>
            <p className="text-muted-foreground">Dein Konto ist aktiv. Du wirst direkt weitergeleitet…</p>
          </div>
        )}

        {state === 'error' && (
          <div data-testid="verify-error" className="space-y-5">
            <CircleAlert className="w-10 h-10 text-amber-400 mx-auto" />
            <h2 className="font-heading font-bold text-2xl">Bestätigung nicht möglich</h2>
            <p className="text-muted-foreground text-sm">{message}</p>
            <div className="space-y-3 text-left">
              <Input
                type="email"
                placeholder="name@firma.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-background"
                data-testid="resend-email-input"
              />
              <Button
                onClick={handleResend}
                disabled={resending || !email}
                className="w-full h-12 font-bold tracking-wider uppercase"
                data-testid="resend-verification-btn"
              >
                {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Bestätigungs-Link neu senden
              </Button>
            </div>
            <Link to="/login" className="text-primary hover:underline text-sm" data-testid="back-to-login-link">
              Zurück zur Anmeldung
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
